import React, { Fragment, memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { capitalize } from 'lodash';
import DeleteIcon from '@mui/icons-material/Delete';
import { showConfirmationDialog } from '@actions/confirmation-actions';
import { fetchDataDetail } from '@actions/data-detail-actions';
import {
  getConfirmableRemoveEntityFromGroupWithMessage,
  openGroupAddEntityDialog
} from '@actions/groups-actions';
import TableHeader from '@components/entity/table-header';
import { getEntityTypeLabel } from '@constants/config';
import { GROUPS_TAB_COLUMNS as columns } from '@constants/table';
import AddToGroupDialog from '@shared/dialogs/add-to-group-dialog';
import Table from '@shared/table';
import { arrayToObjectIds } from '@utils/data-types-utils';
import { openInNewTab } from '@utils/navigation-utils';
import { canEditGroups } from '@utils/permission-utils';

// eslint-disable-next-line no-unused-vars
const onRowClick = (event, row, index) => openInNewTab(`/group/${row.type_name}/${row.id}`);

const Groups = () => {
  const dispatch = useDispatch();
  const { dataType } = useParams();
  const { data } = useSelector(state => state.dataDetail);
  const { list, loading } = useSelector(state => state.groups);

  const dataTypeDisplayName = useMemo(() => getEntityTypeLabel(dataType), [dataType]);

  const rows = useMemo(() => {
    const groupsById = arrayToObjectIds(list);
    return Object.values(groupsById).filter(group => data?.group_ids?.includes(group.id));
  }, [data?.group_ids, list]);

  // Callback for when we successfully added this entity to a group.
  const reloadCallback = useCallback(
    () => dispatch(fetchDataDetail(dataType, data?.id)),
    [data?.id, dataType, dispatch]
  );

  const onAdd = useCallback(
    () => dispatch(openGroupAddEntityDialog(dataType, data, reloadCallback)),
    [data, dataType, dispatch, reloadCallback]
  );

  const entityTypeName = useMemo(() => capitalize(dataType), [dataType]);

  const removeGroup = useCallback(row => dispatch(showConfirmationDialog(
    'Remove From Group',
    `Are you sure you want to remove ${dataType} ${data?.id} from group "${row.name}"?`,
    getConfirmableRemoveEntityFromGroupWithMessage(
      data?.id,
      row.id,
      `${entityTypeName} ${data?.id} removed from the group`,
      reloadCallback
    )
  )), [data?.id, dataType, dispatch, entityTypeName, reloadCallback]);

  const actions = useMemo(() => [
    {
      icon: DeleteIcon,
      checkPermission: row => canEditGroups(row.type_name),
      label: 'Remove entity from group',
      onClick: removeGroup
    }
  ], [removeGroup]);

  if (!data) {
    return null;
  }

  return (
    <Fragment>
      <Table
        actions={actions}
        columns={columns}
        empty={{ message: `This ${dataTypeDisplayName} doesn't belong to any groups yet` }}
        header={<TableHeader count={rows.length} label="group" onAdd={onAdd} tooltip="Add to group" />}
        loading={loading}
        onRowClick={onRowClick}
        rows={rows}
        style={{ maxWidth: '50rem' }}
      />
      <AddToGroupDialog />
    </Fragment>
  );
};

export default memo(Groups);
